import { articles as generatedArticles, events as generatedEvents } from "../generated/editorial";
import type { ProgrammeSlide } from "./programme-galleries";

export interface Article {
  slug: string;
  title: string;
  summary: string;
  publishedDate: string;
  category: string;
  author: string;
  keywords: string[];
  featured: boolean;
  externalUrl?: string;
  image?: string;
  readingMinutes?: number;
  html?: string;
  searchText: string;
}

export interface MissionEvent {
  slug: string;
  title: string;
  summary: string;
  startDate: string;
  endDate?: string;
  time?: string;
  timezone?: string;
  venue: string;
  address?: string;
  mapUrl?: string;
  registrationUrl?: string;
  image?: string;
  gallery: ProgrammeSlide[];
  html?: string;
}

export const articles: Article[] = generatedArticles.map((article) => ({
  ...article,
  keywords: [...article.keywords],
}));
export const events: MissionEvent[] = generatedEvents.map((event) => ({
  ...event,
  gallery: (event.gallery as readonly ProgrammeSlide[]).map((slide) => ({ ...slide })),
}));

export function articleHref(article: Article) {
  return article.externalUrl ?? `/articles/${article.slug}/`;
}

export function eventHref(event: MissionEvent) {
  return `/events/${event.slug}/`;
}

export function formatEditorialDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

export function formatEventDateRange(event: MissionEvent) {
  if (!event.endDate || event.endDate === event.startDate) return formatEditorialDate(event.startDate);
  return `${formatEditorialDate(event.startDate)} - ${formatEditorialDate(event.endDate)}`;
}

export function isUpcomingEvent(event: MissionEvent, today = new Date().toISOString().slice(0, 10)) {
  return (event.endDate ?? event.startDate) >= today;
}

export function normalisePathname(pathname: string) {
  const withoutQuery = pathname.split(/[?#]/)[0];
  if (withoutQuery === "/") return "/";
  return `/${withoutQuery.split("/").filter(Boolean).join("/")}/`;
}

export const editorialRoutes = [
  "/",
  "/articles/",
  "/events/",
  "/gallery/",
  ...articles.filter((article) => !article.externalUrl).map((article) => `/articles/${article.slug}/`),
  ...events.map((event) => `/events/${event.slug}/`),
];

export interface PageMetadata {
  title: string;
  description: string;
  canonical: string;
  type: "website" | "article";
  image?: string;
  publishedTime?: string;
  jsonLd?: Record<string, unknown>;
}

const origin = "https://sridevinatha.org";

export function getPageMetadata(pathname: string): PageMetadata {
  const path = normalisePathname(pathname);
  if (path === "/articles/") {
    return {
      title: "Articles — Sri Devinatha Mission",
      description: "Articles and reflections on Vedic practice, Śrīvidyā and the Mission’s living tradition.",
      canonical: `${origin}/articles/`,
      type: "website",
    };
  }
  if (path === "/events/") {
    return {
      title: "Events — Sri Devinatha Mission",
      description: "Upcoming and past programmes, observances and gatherings of Sri Devinatha Mission.",
      canonical: `${origin}/events/`,
      type: "website",
    };
  }
  if (path === "/gallery/") {
    return {
      title: "Gallery — Sri Devinatha Mission",
      description: "Photographs of Sri Devinatha Mission programmes, learning, service and gatherings.",
      canonical: `${origin}/gallery/`,
      type: "website",
    };
  }
  const articleMatch = path.match(/^\/articles\/([^/]+)\/$/);
  const article = articleMatch ? articles.find((item) => item.slug === articleMatch[1]) : undefined;
  if (article && !article.externalUrl) {
    return {
      title: `${article.title} — Sri Devinatha Mission`,
      description: article.summary,
      canonical: `${origin}${path}`,
      type: "article",
      image: article.image,
      publishedTime: article.publishedDate,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.summary,
        datePublished: article.publishedDate,
        author: { "@type": "Organization", name: article.author },
        publisher: { "@type": "Organization", name: "Sri Devinatha Mission", url: origin },
        mainEntityOfPage: `${origin}${path}`,
      },
    };
  }
  const eventMatch = path.match(/^\/events\/([^/]+)\/$/);
  const event = eventMatch ? events.find((item) => item.slug === eventMatch[1]) : undefined;
  if (event) {
    return {
      title: `${event.title} — Sri Devinatha Mission`,
      description: event.summary,
      canonical: `${origin}${path}`,
      type: "website",
      image: event.image,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.title,
        description: event.summary,
        startDate: event.startDate,
        ...(event.endDate ? { endDate: event.endDate } : {}),
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: event.venue,
          ...(event.address ? { address: event.address } : {}),
        },
        organizer: { "@type": "Organization", name: "Sri Devinatha Mission", url: origin },
        url: `${origin}${path}`,
      },
    };
  }
  return {
    title: "Sri Devinatha Mission — A living Vedic tradition",
    description: "Sri Devinatha Mission carries forward a living Vedic tradition through spiritual practice, teaching, service and the Mahathee Veda Pāṭhaśālā.",
    canonical: `${origin}/`,
    type: "website",
  };
}
